import React, { useMemo, useState } from "react";
import { Alert, Button, Card, Flex, Modal, Table, Typography, Space, Tag, Upload, message } from "antd";
import {
  ReloadOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  InboxOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import type { UploadFile, UploadProps } from "antd";
import { formatDateTimeISO } from "../../../utils/date";
import { useGetWebshopUploadJobsQuery } from "../../../api/webshop/wsProductsApi";
import handleUploadFile from "../../../utils/uploadFileS3";
import { BASE_URL } from "../../../api/emptyV2Api";
import ReplaceModeCheckbox from "./ReplaceModeCheckbox";

const { Title, Text } = Typography;
const { Dragger } = Upload;

const ENDPOINT = "webshop/products/upload?key=input";

interface WebshopUploadsPageProps {
  onBack?: () => void;
  onUploadSuccess?: () => void;
}

export default function WebshopUploadsPage({ onBack, onUploadSuccess }: WebshopUploadsPageProps) {
  const navigate = useNavigate();
  const { data = [], error, isFetching, refetch } = useGetWebshopUploadJobsQuery();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isReplace, setIsReplace] = useState(false);

  // Sort jobs by updatedAt timestamp in descending order (newest first)
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const timeA = a.UpdatedAt || "";
      const timeB = b.UpdatedAt || "";
      return timeB.localeCompare(timeA);
    });
  }, [data]);

  const handleUpload = async () => {
    if (fileList.length === 0) return;
    setIsUploading(true);
    try {
      const fileToUpload = fileList[0] as unknown as File;
      const result = await handleUploadFile(
        BASE_URL + ENDPOINT,
        fileToUpload,
        isReplace ? true : undefined,
      );
      if (!result) {
        message.error("Failed to upload file. Please try again.");
        return;
      }
      message.success("File uploaded successfully. Ingestion in progress.");
      setFileList([]);
      refetch();
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error("Failed to upload file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const internalProps: UploadProps = {
    name: "file",
    multiple: false,
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isValidFile =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".csv") ||
        file.type === "text/csv";

      if (isValidFile) {
        setFileList([file]);
      } else {
        message.error(`${file.name} is not a valid Excel or CSV file`);
      }
      return false;
    },
    fileList
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/webshop/products");
    }
  };

  const columns = [
    {
      title: "Job ID",
      dataIndex: "Id",
      key: "Id",
      render: (id: string) => <Text code style={{ fontSize: "12px" }}>{id}</Text>,
    },
    {
      title: "Started At",
      dataIndex: "ProcessedStartedAt",
      key: "ProcessedStartedAt",
      render: (v: string) => v ? formatDateTimeISO(v) : "-",
    },
    {
      title: "Finished At",
      dataIndex: "ProcessedFinishedAt",
      key: "ProcessedFinishedAt",
      render: (v: string) => v ? formatDateTimeISO(v) : "-",
    },
    {
      title: "Replace Mode",
      dataIndex: "IsReplace",
      key: "IsReplace",
      render: (isReplace: boolean) => (
        <Tag color={isReplace ? "blue" : "purple"}>
          {isReplace ? "REPLACE" : "APPEND"}
        </Tag>
      ),
    },
    {
      title: "Ingested Rows",
      dataIndex: "IngestedRows",
      key: "IngestedRows",
      render: (count: number) => <Text strong>{count || 0}</Text>,
    },
    {
      title: "Error Rows",
      dataIndex: "ErrorRows",
      key: "ErrorRows",
      render: (count: number) => (
        <Text type={count > 0 ? "danger" : "secondary"}>
          {count || 0}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      render: (status: string) => {
        let color = "default";
        let icon = null;
        if (status === "Completed") {
          color = "success";
          icon = <CheckCircleOutlined />;
        } else if (status === "Processing") {
          color = "processing";
          icon = <SyncOutlined spin />;
        } else if (status && status.includes("Error")) {
          color = "error";
          icon = <ExclamationCircleOutlined />;
        }
        return (
          <Tag icon={icon} color={color}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, obj: any) => {
        const hasErrors = obj.Errors && obj.Errors.length > 0;
        
        const onClickViewErr = () => {
          Modal.error({
            title: "Ingestion Error Log",
            width: 800,
            content: (
              <div style={{ maxHeight: "400px", overflowY: "auto", marginTop: "12px" }}>
                <ul style={{ paddingLeft: "20px" }}>
                  {obj.Errors.map((err: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: "8px", color: "#cf1322" }}>
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            okText: "Close"
          });
        };

        return (
          <Button
            icon={<EyeOutlined />}
            disabled={!hasErrors}
            onClick={onClickViewErr}
            type="link"
          >
            View Errors
          </Button>
        );
      },
    },
  ];

  return (
    <Flex gap={16} vertical style={{ width: "100%" }}>
      <Flex align="center" justify="space-between">
        <Space size={12}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Webshop Products Uploads & History
            </Title>
            <Text type="secondary">
              Upload product sheets and monitor real-time processing and errors.
            </Text>
          </div>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={refetch} loading={isFetching}>
          Reload Logs
        </Button>
      </Flex>

      {error && (
        <Alert
          message="Failed to load upload history"
          description="We encountered an issue fetching the S3 ingestion logs. Please try again."
          type="error"
          showIcon
        />
      )}

      <Card
        title="Upload Products File"
        bordered={false}
        style={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          borderRadius: "8px",
        }}
      >
        <Flex vertical gap={16}>
          <Dragger {...internalProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "rgb(255, 70, 37)" }} />
            </p>
            <p className="ant-upload-text">Click or drag Webshop Products file here</p>
            <p className="ant-upload-hint">
              Supports Excel (.xls, .xlsx) and CSV files.
            </p>
          </Dragger>
          <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
            <ReplaceModeCheckbox
              checked={isReplace}
              onChange={setIsReplace}
              disabled={isUploading}
            />
            <Button
              type="primary"
              onClick={handleUpload}
              disabled={fileList.length === 0 || isUploading}
              loading={isUploading}
              style={{ backgroundColor: "rgb(255, 70, 37)", borderColor: "rgb(255, 70, 37)" }}
            >
              Upload & Process
            </Button>
          </Flex>
        </Flex>
      </Card>

      <Card
        title="Ingestion Jobs"
        bordered={false}
        bodyStyle={{ padding: 0 }}
        style={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <Table
          rowKey="Id"
          loading={isFetching}
          dataSource={sortedData}
          columns={columns}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
          }}
        />
      </Card>
    </Flex>
  );
}
